import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Button, message, Modal } from 'antd';
import { findIndex, sortBy } from 'lodash';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { EventContext } from '.';
import { generateMatches, getPositions, getVolleyballPositions, startTournament } from 'api/events';
import { addParticipantToGroup, getParticipants } from 'api/teams';
import { NewGroupModal } from 'componentsv2/util-components/NewGroupModal/NewGroupModal';
import GroupCardContainer from 'componentsv2/GroupCardContainer/GroupCardContainer';

const eventAction = {
  true: 'iniciar',
  false: 'detener',
};

const ScrumboardWrapper = props => {
  const { eventId, reloadGroups, sortedGroups } = props;

  const onDragEnd = async result => {
    if (!result.destination) {
      message.error('!Por favor arrastrar a una locacion valida!');
      return;
    }

    const source = result.source;
    const destination = result.destination;

    if (source.droppableId !== destination.droppableId && !destination.droppableId.includes('PT')) {
      let roundsGenerated = sortedGroups.find(
        group => group.id === Number(destination.droppableId) && group?.rounds_generated === false,
      );
      if (roundsGenerated) {
        let isInvalidDrop = roundsGenerated?.tournament_club_groups.find(
          clubGroup => clubGroup.club.id === Number(result.draggableId),
        );
        if (!isInvalidDrop) {
          message.loading('Agregando equipo', 1);
          try {
            await addParticipantToGroup(eventId, destination.droppableId, result.draggableId);
            await reloadGroups();
            message.success('!El equipo fue agregado!');
          } catch (error) {
            message.error('!Ha ocurrido un error!');
          }
        } else {
          message.error('!Por favor arrastrar a un grupo valido!');
        }
      } else {
        message.error('!No se puede puede agregar si el grupo ya tiene jornadas!');
      }
    } else {
      message.error('!Por favor arrastrar a una locacion valida!');
    }

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={result => onDragEnd(result)}>
        {props.containerHeight ? (
          <div className="scrumboard">
            <BoardWrapper {...props} />
          </div>
        ) : (
          <BoardWrapper {...props} />
        )}
      </DragDropContext>
    </>
  );
};

const BoardWrapper = ({
  eventId,
  sortedGroups,
  containerHeight,
  useClone,
  isCombineEnabled,
  withScrollableColumns,
  event,
  reloadGroups,
  reloadEvent,
}) => {
  const {
    event: { sport_name },
  } = useContext(EventContext);

  const [isCreateGroupModal, setIsCreateGroupModal] = useState(false);

  const [isStartingEvent, setIsStartingEvent] = useState(false);

  const fetchPositions = useCallback(async (key, id) => {
    const { body } = await getPositions(id);

    return body?.data?.position_tables || {};
  }, []);

  const fetchPositionsVoleyball = useCallback(async (key, _eventId) => {
    const { body } = await getVolleyballPositions(_eventId);

    return body?.data?.position_tables || [];
  }, []);

  const { data } = useQuery(
    ['event-positions', eventId],
    sport_name === 'Volleyball' ? fetchPositionsVoleyball : fetchPositions,
  );

  const resolver = useCallback(async (key, _eventId) => {
    const { body } = await getParticipants(_eventId);

    return body?.data?.participants || [];
  }, []);

  const { data: participantsData, refetch } = useQuery(['event-participants', eventId], resolver);

  const secondHeadingIndex = useMemo(() => findIndex(sortedGroups, g => !g.play_off), [
    sortedGroups,
  ]);
  const hasPhasedGroups = useMemo(() => sortedGroups.filter(group => group.play_off).length > 0, [
    sortedGroups,
  ]);

  const getGroupNameBId = useCallback(
    gId => {
      const _group = sortedGroups.filter(g => g.id === gId)[0];

      return _group?.name || `Grupo ${gId}`;
    },
    [sortedGroups],
  );

  const setEventStatus = async (eventId, started) => {
    setIsStartingEvent(true);
    try {
      await startTournament(eventId, started);
      await reloadEvent();
      await reloadGroups();
      setIsStartingEvent(false);
      started ? message.success('!Evento iniciado!') : message.success('!Evento detenido!');
    } catch (error) {
      setIsStartingEvent(false);
      message.error(`!Ha ocurrido un error al ${eventAction[started]} el torneo!`);
    }
  };

  const onCreateJornada = eventId => {
    Modal.confirm({
      title:
        '¿Estas seguro que deseas generar las jornadas de todos los grupos? Esta acción no se podrá revertir.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Si',
      cancelText: 'Cancelar',
      async onOk() {
        try {
          const { status, body } = await generateMatches(eventId);
          const isSuccess = [200, 201].includes(status);
          const badRequest = [400].includes(status);
          let messages = body?.data?.results || [];
          if (isSuccess && messages.length > 0) {
            message.success('Jornadas creadas!');
            messages.map((message, idx) => {
              const roundsWereGenerated =
                String(message?.message).indexOf('Rounds generated for group') !== -1;
              const notEnoughClubs =
                String(message?.message).indexOf(
                  'unable to generate rounds for less than 2 clubs',
                ) !== -1;

              if (roundsWereGenerated) {
                message.success(
                  `Rondas generadas para el grupo ${getGroupNameBId(message?.group_id)}`,
                );
              } else if (notEnoughClubs) {
                message.error(`${getGroupNameBId(message?.group_id)}: No se puede generar
                jornadas en grupos con menos de 2 participantes.`);
              }
              return null;
            });
            message.success('Las jornadas han sido creadas!');
          } else if (isSuccess && messages.length === 0) {
            message.success('No se detectaron cambios!');
          } else if (badRequest) {
            message.success(`!Verificar creación de jornadas!`);
          }
          await reloadGroups();
        } catch (error) {
          message.error('!Ha ocurrido un error!');
        }
      },
    });
  };

  return (
    <Droppable
      droppableId="board"
      type="COLUMN"
      direction="horizontal"
      ignoreContainerClipping={containerHeight}
      isCombineEnabled={isCombineEnabled}
    >
      {provided => (
        <div className="scrumboard" ref={provided.innerRef} {...provided.droppableProps}>
          <NewGroupModal
            isVisible={isCreateGroupModal}
            handleOk={setIsCreateGroupModal}
            refetch={reloadGroups}
          />
          <div className="scrumboard-header">
            <div>
              {!event.started ? (
                <Button
                  loading={isStartingEvent}
                  onClick={() => setEventStatus(eventId, true)}
                  type="primary"
                  danger
                >
                  INICIAR EVENTO
                </Button>
              ) : (
                <Button
                  loading={isStartingEvent}
                  onClick={() => setEventStatus(eventId, false)}
                  type="primary"
                  danger
                >
                  DETENER EVENTO
                </Button>
              )}
              <Button type="primary" className="ml-1" onClick={() => setIsCreateGroupModal(true)}>
                CREAR NUEVO GRUPO
              </Button>
              <Button onClick={() => onCreateJornada(eventId)} type="primary" className="ml-1">
                CREAR JORNADAS
              </Button>
            </div>
            <div className="text-right">
              <div className="d-flex align-items-center"></div>
            </div>
          </div>
          <div className="scrumboard-body">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '50%',
                alignItems: 'center',
              }}
            >
              {sortedGroups.map((group, index) => {
                return (
                  <div
                    key={index}
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {hasPhasedGroups && event?.started && index === 0 && (
                      <h2 className="uppercase text-gray-600 text-lg mb-4">
                        Grupos de eliminatoria
                      </h2>
                    )}
                    {hasPhasedGroups && event?.started && index === secondHeadingIndex && (
                      <h2 className="uppercase text-gray-600 text-lg mb-4">
                        Grupos de clasificación
                      </h2>
                    )}
                    <GroupCardContainer
                      key={group.id}
                      index={index}
                      title={group.name}
                      id={group.id}
                      contents={group?.tournament_club_groups}
                      roundsGenerated={group?.rounds_generated}
                      isScrollable={withScrollableColumns}
                      isCombineEnabled={isCombineEnabled}
                      useClone={useClone}
                      isPositionTable={false}
                      event={event}
                      reloadGroups={reloadGroups}
                    />
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '50%',
                alignItems: 'center',
              }}
            >
              <h2 className="uppercase text-gray-600 text-lg mb-4">
                {event?.started ? 'Tabla de posiciones' : 'Todos los participantes'}
              </h2>
              {event?.started ? (
                sortedGroups.map((group, index) => {
                  const positions = data ? data[group.id] || [] : [];
                  // if (positions && positions.length > 0)
                  if (!group.play_off) {
                    return (
                      <GroupCardContainer
                        key={group.id}
                        index={index}
                        title={group.name}
                        id={group.id}
                        contents={positions.length > 0 ? positions : {}}
                        isScrollable={withScrollableColumns}
                        isCombineEnabled={isCombineEnabled}
                        useClone={useClone}
                        isPositionTable={true}
                        event={event}
                        reloadGroups={reloadGroups}
                      />
                    );
                  }
                })
              ) : (
                <GroupCardContainer
                  key={Math.random()}
                  title={'Participantes'}
                  id={Math.random()}
                  index={0}
                  contents={participantsData}
                  isScrollable={withScrollableColumns}
                  isCombineEnabled={isCombineEnabled}
                  useClone={useClone}
                  isPositionTable={true}
                  event={event}
                  reloadGroups={reloadGroups}
                />
              )}
            </div>
            {provided.placeholder}
          </div>
        </div>
      )}
    </Droppable>
  );
};

const Groups = props => {
  const { eventId, reloadGroups, groups, event, reloadEvent } = useContext(EventContext);
  const sortedGroups = useMemo(() => sortBy(groups, g => !g.play_off), [groups]);

  return (
    <ScrumboardWrapper
      eventId={eventId}
      event={event}
      reloadGroups={reloadGroups}
      reloadEvent={reloadEvent}
      sortedGroups={sortedGroups}
      {...props}
    />
  );
};

export default Groups;
